/// <amd-module name="Dashboards/Directory/components/DashboardDirectoryService" />
import {  IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { Dashboard } from "../DashboardsDirectoryContracts";
import { SortOrder } from "VSSUI/Table";
import { IFavoriteData } from "VSS/Features/Directory/DirectoryContracts";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IDashboardDirectoryService extends IVssService {

    /*  Filter object for filtering dashboards*/
    filter: Filter; 

    /*Observable array to hold the rows of dashboards */
    rows: IObservableArray<Dashboard>;

    /* Method to load dashboards data */
    loadDashboards(data: Dashboard[], fav: { [key: string]: IFavoriteData },  disableDefaultSorting: boolean): void;

    /* Method to sort the rows of dashboards */
    sortRows(
        columnIndex: number,
        sortOrder: SortOrder,
        sortFunction: (columnIndex: number, order: SortOrder, rows: Dashboard[]) => Dashboard[]
    ): void;

    /* Method to delete a dashboard */
    deleteDashboard(id: string, _onDeleteDashboardClickRow : (row: Dashboard) => void): void;
}

class DashboardDirectoryService extends VssService implements IDashboardDirectoryService {

    private _filter: Filter;
    private _dashboardData: Dashboard[];
    private _rows: IObservableArray<Dashboard>;
    private _favorites : { [key: string]: IFavoriteData };
    private performanceService : IVssPerformanceService;

    // feature flag for new dashboard Directory (NWP)
    private  newDashboardDirectorySortTelemetryFeatureFlag = "WebAccess.Dashboards.NWPSortingTelemetry";

    // feature flag for new dashboard Directory (NWP)
    private newDashboardDirectoryFilterTelemetryFeatureFlag = "WebAccess.Dashboards.NWPFilterTelemetry";

    // fields required for sorting
    private _sort: ((columnIndex: number, order: SortOrder, rows: Dashboard[]) => Dashboard[]) | any;
    private _sortColumnIndex: number;
    private _sortOrder: SortOrder; // Sort order (ascending or descending)

    private deleteDashboardFlag: any;

    public get filter(): Filter {
        return this._filter;
    }

    public get rows(): IObservableArray<Dashboard> {
        return this._rows;
    }

    loadDashboards(data: Dashboard[], fav : { [key: string]: IFavoriteData }, disableDefaultSorting: boolean): void {
        if(data && !disableDefaultSorting)
        {
            this._favorites = fav;
            this._dashboardData = data;
            let result = this.addInitialSorting();
            if(result && result.length > 0)
            {
                this._dashboardData = result;
                this._rows.value = result;
                this._updateDisplayedRows();
            }
        }
        else if(data && this.deleteDashboardFlag != null)
        {
            this._rows.value.splice(this.deleteDashboardFlag, 1);
            this._dashboardData = [...data];
            this._updateDisplayedRows();
            this.deleteDashboardFlag = null;
        }
        else if(data)
        {
            this._rows.value = this._rows.value;
        }
    }

    private addInitialSorting() 
    {
        let result: Dashboard[] = [];
        if(this._dashboardData && this._favorites)
        {
            const nonFavArray: Dashboard[] = [];
            const favArray: Dashboard[] = [];
            this._dashboardData.forEach((item) => {
                if (this._favorites[item.id]) {
                    favArray.push(item);
                } else {
                    nonFavArray.push(item);
                }
            });
            nonFavArray.sort((a, b) => a.name.localeCompare(b.name, navigator.language));
            favArray.sort((a, b) => a.name.localeCompare(b.name, navigator.language)); // fav dashboards
            result = [...favArray, ...nonFavArray];
        }
        else if(this._dashboardData)
        {
            result = this._dashboardData.sort((a, b) => a.name.localeCompare(b.name, navigator.language));
        }
        return result;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._filter = new Filter();
        this._dashboardData = [];
        this._rows = new ObservableArray([]);
        this._favorites = {};
        this._sortColumnIndex = 1;
        this._sortOrder = SortOrder.ascending;

        this._filter.subscribe(this._onFilterChanged);
        this.performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this.deleteDashboardFlag = null;
    }

    public _serviceEnd() {
        this._filter.unsubscribe(this._onFilterChanged);
    }

    public sortRows(
        columnIndex: number,
        sortOrder: SortOrder,
        sortFunction: (columnIndex: number, order: SortOrder, rows: Dashboard[]) => Dashboard[]
    ): void {
        // save off the sort data because we will need to re-use on filtering
        this._sortColumnIndex = columnIndex;
        this._sortOrder = sortOrder;
        this._sort = sortFunction;

        let isFFEnabled = isFeatureFlagEnabled(this.pageContext, this.newDashboardDirectorySortTelemetryFeatureFlag, false);
        if(isFFEnabled){
            this.performanceService.startScenario("dashboard-directory-sort", false);
        }

        this._updateDisplayedRows();

        if(isFFEnabled){
            this.performanceService.endScenario("Dashboards","dashboard-directory-sort", false, undefined, 
            { 
                "sortColumnIndex": columnIndex,
                "sortOrder" : sortOrder,
                "dashboardCount" : this._rows.length
            });
        }
    }

    public deleteDashboard(id: string, _onDeleteDashboardClickRow : (row: Dashboard) => void): void {
        const index = this._rows.value.findIndex(p => p.id === id);
        const dashboardsListIndex = this._dashboardData.findIndex(p => p.id === id);
        if (index >= 0 && dashboardsListIndex >= 0) {
            const rowData = this._rows.value[index];
            try {
                // optimistically remove row
                _onDeleteDashboardClickRow;
                this.deleteDashboardFlag = index;
            } catch (ex) {
                // set the error and add the row back
                this._rows.splice(index, 0, rowData);
                this.deleteDashboardFlag = null;
            }
        }
    }

    private _updateDisplayedRows() {
        const filteredDashboards = this._applyFilter(this._filter.getAppliedState());
        const sortedDashboards = this._sort ? this._sort(this._sortColumnIndex, this._sortOrder, filteredDashboards) : filteredDashboards;
        this._rows.value = [...sortedDashboards];
    }

    private _onFilterChanged = (filterState: IFilterState, action: string) => {
        if (action === "filter-applied") {
            let isFFEnabled = isFeatureFlagEnabled(this.pageContext, this.newDashboardDirectoryFilterTelemetryFeatureFlag, false);
            if(isFFEnabled){
              this.performanceService.startScenario("dashboard-directory-filter", false);
            }
            let initialDashboardsCount = this._rows.length;
            this._updateDisplayedRows();

            if(isFFEnabled){
                this.performanceService.endScenario("Dashboards","dashboard-directory-filter", false, undefined, 
                { 
                    "filterState": this._filter.getAppliedState(), // filter state, initial dashboard data count, filtered dashboard count
                    "initialDashboardsCount" : initialDashboardsCount,
                    "filteredDashboardCount" : this._rows.length
                });
            }
        }
    };

    private _applyFilter(filterState: IFilterState): Dashboard[] {
        let filterValue = filterState.Keyword ? filterState.Keyword.value : ''; 
        let filterOnTeam = filterState.Team ? filterState.Team.value : '';
        let filterOnDashboardType = filterState.DashboardType ? filterState.DashboardType.value : ''
        let filteredData = [...this._dashboardData];

        if(filterOnTeam != ''){
            filteredData =  [...this._dashboardData.filter(obj => filterOnTeam.some((str: string) => str === obj.ownerId))];
        }

        if(filterOnDashboardType != ''){
            filteredData =  filteredData.filter((item) => {
                if((item.dashboardScope == 1 && filterOnDashboardType.includes('Team')) || (item.dashboardScope == 2 && filterOnDashboardType.includes('Project')))
                {
                    return true;
                }
                else{
                    return false;
                }   
            });
        }

        return this._filterdashboards(filterValue, filteredData);
    }

    private _filterdashboards(filterText: string, existingFilteredData: Dashboard[]): Dashboard[] {
        if (filterText === "") {
            return existingFilteredData;
        } else {
            const filterTextToUpper = filterText.toLocaleUpperCase();
            return existingFilteredData.filter(p => this._matchFilterText(p, filterTextToUpper));
        }
    }

    // text filter
    private _matchFilterText(dashboardData: Dashboard, filterText: string): boolean {
        return (
            dashboardData.name.toLocaleUpperCase().includes(filterText) ||
            dashboardData.description?.toLocaleUpperCase().includes(filterText)
        );
    }
}

const serviceName = "IDashboardDirectoryService";
Services.add(serviceName, { serviceFactory: DashboardDirectoryService });
